import useJobsOverview from "../../../hooks/useJobsOverview";
import { useGtinStatus } from "../../../features/admin/articles/hooks/useArticles";
import { useNotificationBadges } from "../../../hooks/useNotificationBadges";
import { useReviewableDraftCount } from "../../../features/admin/wizard/hooks/useListingDrafts";
import { useReviewableDesignRunCount } from "../../../features/design-generator/hooks/useReviewableDesignRunCount";

export type MerchantBadgeTone = "primary" | "success" | "warning" | "error" | "neutral";

export interface MerchantNavigationBadge {
  count?: number;
  dot?: boolean;
  tone: MerchantBadgeTone;
  label: string;
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

export function useMerchantNavigationBadges(isAdmin: boolean, t: Translate) {
  const overview = useJobsOverview({ isAdmin });
  const gtin = useGtinStatus({ enabled: isAdmin });
  const badges = useNotificationBadges({ enabled: isAdmin });
  const reviewableDrafts = useReviewableDraftCount({ enabled: isAdmin });
  const reviewableDesignRuns = useReviewableDesignRunCount({ enabled: isAdmin });

  const result: Record<string, MerchantNavigationBadge | undefined> = {};
  const openMessages = Number(badges.data?.messagesOpen || 0);
  const actionableReturns = Number(badges.data?.returnsActionable || 0);
  const openOrders = Number(badges.data?.ordersOpen || 0);
  const advertisingAttention = Number(badges.data?.advertisingAttention || 0);
  const advertisingConnectionIssue = Boolean(badges.data?.advertisingConnectionIssue);
  const reviewableCount = Number(reviewableDrafts.data || 0);
  const reviewRuns = Number(reviewableDesignRuns.data || 0);
  const readyCount = Number(overview.readyDownloads?.length || 0);
  const gtinLow = Boolean(
    isAdmin && gtin.data && gtin.data.free < Math.max(100, gtin.data.reserved),
  );

  if (openOrders > 0) {
    result.orders = {
      count: openOrders,
      tone: "primary",
      label: t("layout.sidebar.ordersBadgeAria", { count: openOrders }),
    };
  }
  if (openMessages > 0) {
    result.messages = {
      count: openMessages,
      tone: "primary",
      label: t("layout.sidebar.messagesBadgeAria", { count: openMessages }),
    };
  }
  if (actionableReturns > 0) {
    result.returns = {
      count: actionableReturns,
      tone: "primary",
      label: t("layout.sidebar.returnsBadgeAria", { count: actionableReturns }),
    };
  }
  if (advertisingAttention > 0 || advertisingConnectionIssue) {
    result.advertising = {
      count: advertisingAttention || undefined,
      dot: advertisingConnectionIssue && advertisingAttention === 0,
      tone: advertisingConnectionIssue ? "error" : "warning",
      label: advertisingConnectionIssue
        ? t("layout.sidebar.advertisingConnectionAria")
        : t("layout.sidebar.advertisingBadgeAria", { count: advertisingAttention }),
    };
  }
  if (reviewableCount > 0) {
    result.wizard = {
      count: reviewableCount,
      tone: "success",
      label: t("layout.sidebar.wizardBadgeAria", { count: reviewableCount }),
    };
  }
  if (reviewRuns > 0) {
    result["design-generator"] = {
      count: reviewRuns,
      tone: "success",
      label: t("layout.sidebar.studioBadgeAria", { count: reviewRuns }),
    };
  }
  if (gtinLow) {
    result.articles = {
      dot: true,
      tone: "warning",
      label: t("layout.merchantShell.gtinAttention"),
    };
  }
  if (readyCount > 0) {
    result.downloads = {
      count: readyCount,
      tone: "neutral",
      label: t("layout.sidebar.downloadsBadgeAria", { count: readyCount }),
    };
  }

  return result;
}
